import { useTranslation } from "react-i18next";
import { useInvestmentsSummary } from "../../hooks/investments";
import { formatEur, formatPercent } from "../../lib/format-kpi";
import type { InvestmentListItem } from "../../lib/investments";
import { computeDepotAggregate } from "./DepotKpiCard";
import { computeErtraegeAggregate } from "./ErtraegeKpiCard";

/**
 * 4-Spalten-Stats-Strip an Stelle der frueheren 3 KPI-Cards.
 * Spalten: Depotwert / Gewinn seit Kauf / Dividenden YTD / Realisiert YTD.
 * XIRR als Sekundaer-Info unter "Gewinn seit Kauf".
 */
export function InvestmentsStatsStrip({ items }: { items: InvestmentListItem[] }) {
  const { t } = useTranslation();
  const depot = computeDepotAggregate(items);
  const ertraege = computeErtraegeAggregate(items);
  const summary = useInvestmentsSummary("total");
  const xirr = summary.data?.periodXirrPercent ?? null;

  const dailyPos = depot.dayDiffEur >= 0;
  const totalPos = depot.totalDiffEur >= 0;
  const realizedNeg = ertraege.realizedYtdEur < 0;

  return (
    <div
      className="fv-card grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 divide-y md:divide-y-0 md:divide-x"
      style={{ borderColor: "var(--border)" }}
    >
      {/* Depotwert */}
      <Cell>
        <Label>
          {t("investments.kpi.depot.title", { defaultValue: "Depotwert" })}
        </Label>
        <Value>{formatEur(depot.marketValueEur)}</Value>
        <SubRow>
          {depot.dayDiffPct !== null && (
            <Pill tone={dailyPos ? "pos" : "neg"}>
              {dailyPos ? "↑" : "↓"} {formatPercent(depot.dayDiffPct, 2)}
            </Pill>
          )}
          <span className="text-xs num" style={{ color: "var(--muted)" }}>
            {signedEur(depot.dayDiffEur)} ·{" "}
            {t("investments.kpi.depot.vs_yesterday_short", { defaultValue: "24 h" })}
          </span>
        </SubRow>
      </Cell>

      {/* Gewinn seit Kauf */}
      <Cell>
        <Label>
          {t("investments.kpi.performance.title_short", { defaultValue: "Gewinn seit Kauf" })}
        </Label>
        <Value tone={totalPos ? "pos" : "neg"}>{signedEur(depot.totalDiffEur)}</Value>
        <SubRow>
          {depot.totalDiffPct !== null && (
            <Pill tone={totalPos ? "pos" : "neg"}>{formatPercent(depot.totalDiffPct, 2)}</Pill>
          )}
          {xirr !== null && (
            <span className="text-xs num" style={{ color: "var(--muted)" }}>
              XIRR {formatPercent(xirr, 1)}
            </span>
          )}
        </SubRow>
      </Cell>

      {/* Dividenden YTD */}
      <Cell>
        <Label>
          {t("investments.kpi.ertraege.dividends_ytd_short", { defaultValue: "Dividenden YTD" })}
        </Label>
        <Value tone={ertraege.dividendsYtdEur > 0 ? "pos" : undefined}>
          {formatEur(ertraege.dividendsYtdEur)}
        </Value>
        <SubRow>
          {ertraege.yocLifetimeWertpapierePct !== null && (
            <span className="text-xs num" style={{ color: "var(--muted)" }}>
              YoC {formatPercent(ertraege.yocLifetimeWertpapierePct, 2)} ·{" "}
              {t("investments.kpi.ertraege.lifetime_short", { defaultValue: "ges." })}{" "}
              {formatEur(
                ertraege.dividendsLifetimeWertpapiereEur + ertraege.dividendsLifetimeP2pEur,
              )}
            </span>
          )}
        </SubRow>
      </Cell>

      {/* Realisiert YTD */}
      <Cell>
        <Label>
          {t("investments.kpi.ertraege.realized_ytd_short", { defaultValue: "Realisiert YTD" })}
        </Label>
        <Value tone={realizedNeg ? "neg" : "pos"}>{signedEur(ertraege.realizedYtdEur)}</Value>
        <SubRow>
          <span className="text-xs num" style={{ color: "var(--muted)" }}>
            {t("investments.kpi.ertraege.realized_lifetime_short", {
              defaultValue: "ges. seit Kauf",
            })}{" "}
            {signedEur(ertraege.realizedLifetimeEur)}
          </span>
        </SubRow>
      </Cell>
    </div>
  );
}

function Cell({ children }: { children: React.ReactNode }) {
  return <div className="p-4">{children}</div>;
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="fv-kpi-label mb-1">{children}</div>;
}

function Value({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: "pos" | "neg";
}) {
  const color = tone === "pos" ? "var(--pos)" : tone === "neg" ? "var(--neg)" : "var(--text)";
  return (
    <div className="text-2xl font-bold tracking-tight num" style={{ color }}>
      {children}
    </div>
  );
}

function SubRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2 mt-2">{children}</div>;
}

function Pill({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "pos" | "neg";
}) {
  return (
    <span className={`fv-pill ${tone === "pos" ? "fv-pill-pos" : "fv-pill-neg"}`}>{children}</span>
  );
}

function signedEur(cents: number): string {
  return (cents > 0 ? "+" : "") + formatEur(cents);
}
